import { Platform, StyleSheet } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

const UNDERLINE_HEIGHT = 3

export type UnderlineTabBarLayoutMode = 'equal' | 'intrinsic'

export interface UnderlineTabBarStyleParams {
  containerWidth: number
  tabCount: number
  /** When false, parent supplies the baseline border (e.g. league detail `tabStrip`) */
  includeBottomBorder: boolean
  /** Web-only max width (World Cup chrome); omit for full-bleed */
  webMaxWidth?: number
  layout: UnderlineTabBarLayoutMode
  bottomBorderColor: string
  tabPaddingVertical: number
  tabPaddingHorizontal: number
  tabContentGap: number
  labelFontSize: number
  activeTextColor: string
  inactiveTextColor: string
  underlineColor: string
  backgroundColor?: string
  underlineWebGlow: boolean
}

export const getUnderlineTabBarStyles = (
  _theme: Theme,
  {
    containerWidth,
    tabCount,
    includeBottomBorder,
    webMaxWidth,
    layout,
    bottomBorderColor,
    tabPaddingVertical,
    tabPaddingHorizontal,
    tabContentGap,
    labelFontSize,
    activeTextColor,
    inactiveTextColor,
    underlineColor,
    backgroundColor,
    underlineWebGlow,
  }: UnderlineTabBarStyleParams,
) => {
  const tabWidth =
    containerWidth > 0 && tabCount > 0 ? containerWidth / tabCount : 0

  const glowStyle =
    Platform.OS === 'web' && underlineWebGlow
      ? { boxShadow: `0 0 16px 4px ${underlineColor}99, 0 0 6px ${underlineColor}CC` }
      : {}

  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'stretch',
      ...(backgroundColor != null ? { backgroundColor } : {}),
      ...(includeBottomBorder && {
        borderBottomWidth: 1,
        borderBottomColor: bottomBorderColor,
      }),
      ...(Platform.OS === 'web' &&
        webMaxWidth != null && {
          maxWidth: webMaxWidth,
          width: '100%' as never,
          alignSelf: 'center' as never,
        }),
    },
    intrinsicScroll: {
      flexGrow: 0,
    },
    /** Shrink-wrap total width so tabs don't stretch to fill the viewport (fixes web equal-split bug). */
    intrinsicScrollContent: {
      flexGrow: 0,
      alignItems: 'flex-start',
      ...(Platform.OS === 'web'
        ? ({
            width: 'max-content' as never,
            minWidth: 'max-content' as never,
          } as const)
        : {}),
    },
    intrinsicInner: {
      position: 'relative',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flexGrow: 0,
      ...(Platform.OS === 'web'
        ? ({
            width: 'max-content' as never,
            minWidth: 'max-content' as never,
          } as const)
        : {}),
    },
    intrinsicTabRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      flexGrow: 0,
      flexWrap: 'nowrap',
      ...(Platform.OS === 'web'
        ? ({
            width: 'max-content' as never,
            minWidth: 'max-content' as never,
          } as const)
        : {}),
    },
    tab: {
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: tabPaddingVertical,
      paddingHorizontal: tabPaddingHorizontal,
      ...(layout === 'equal'
        ? { flex: 1 }
        : {
            flexGrow: 0,
            flexShrink: 0,
            alignSelf: 'flex-start',
          }),
    },
    tabPressed: { opacity: 0.8 },
    tabHover: { opacity: 0.9 },
    tabDisabled: {
      opacity: 0.45,
    },
    tabContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: tabContentGap,
    },
    tabBadge: {
      marginLeft: 4,
    },
    tabText: {
      fontSize: labelFontSize,
      fontWeight: '600',
    },
    tabTextActive: {
      color: activeTextColor,
      fontWeight: '700',
    },
    tabTextInactive: {
      color: inactiveTextColor,
    },
    tabUnderlineEqual: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: tabWidth,
      height: UNDERLINE_HEIGHT,
      borderTopLeftRadius: 2,
      borderTopRightRadius: 2,
      backgroundColor: underlineColor,
      ...glowStyle,
    },
    tabUnderlineIntrinsic: {
      position: 'absolute',
      bottom: 0,
      height: UNDERLINE_HEIGHT,
      borderTopLeftRadius: 2,
      borderTopRightRadius: 2,
      backgroundColor: underlineColor,
      ...glowStyle,
    },
  })
}
